import { Modal } from "antd";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { MODAL_NAME } from "./constants";
import {
  StyledBoldHeaderTitle,
  StyledContainer,
  StyledHeader,
  StyledSwitchButton,
} from "./Style";
import { useRouter } from "next/router";
import { getProjectState } from "@/redux/store";
import { projectActions } from "@/redux/stores/projectStore";
import { ApiPriceCell } from "./Cell/ApiPriceCell";
import { useTranslation } from "next-i18next";
import { useGetPlansQuery } from "@/redux/api/paymentApi";
import { useEffect, useMemo, useState } from "react";
import { useGetSubscriptionsQuery } from "@/redux/api/userApi";
import { SubscribeListModalProps } from "./SubscriptionListModal";
import { PaymentsPlan } from "@/types/users";

export const ApiSubscriptionListModal = (props: SubscribeListModalProps) => {
  const { startSubscription, upgradeSubscription, downgradeSubscription } =
    props;
  const router = useRouter();
  const { t } = useTranslation("common");
  const { setOpenedModal } = projectActions;
  const dispatch = useAppDispatch();

  const { data: allPlans, isSuccess } = useGetPlansQuery({
    product: "studio3.0",
  });
  const { data: subscriptions } = useGetSubscriptionsQuery("api");

  // console.log("!@#!@#!@# subscriptions", subscriptions);
  const [apiPlans, setApiPlans] = useState<PaymentsPlan[]>([]);

  const openedModal = useAppSelector(
    (store) => getProjectState(store).openedModal,
  );

  const closeModal = () => {
    if (
      router.query?.openSubscribe === "true" ||
      router.query?.openCheckout === "true"
    ) {
      router.replace(window.location.pathname, undefined, { shallow: true });
    }
    setTimeout(() => {
      dispatch(setOpenedModal(MODAL_NAME.NONE));
    }, 500);
  };

  const changeToAppPlan = () => {
    if (
      router.query?.openSubscribe === "true" ||
      router.query?.openCheckout === "true"
    ) {
      router.replace(window.location.pathname, undefined, { shallow: true });
      setTimeout(() => {
        dispatch(setOpenedModal(MODAL_NAME.SUBSCRIPTION_LIST));
      }, 500);
    } else dispatch(setOpenedModal(MODAL_NAME.SUBSCRIPTION_LIST));
  };

  const currentPlan = useMemo(() => {
    //free, standard, premium, enterprise
    if (!subscriptions) return null;
    if (subscriptions?.plan?.toLocaleLowerCase().includes("standard"))
      return "standard";
    if (subscriptions?.plan?.toLocaleLowerCase().includes("premium"))
      return "premium";
    if (subscriptions?.plan?.toLocaleLowerCase().includes("enterprise"))
      return "enterprise";
    return "free";
  }, [subscriptions]);
  // console.log("!@#!@#!@# subscriptions ", subscriptions);
  // console.log("!@#!@#!@# currentPlan ", currentPlan);

  useEffect(() => {
    if (isSuccess && Array.isArray(allPlans) && allPlans?.length > 0) {
      // console.log("!@#!@#!@# ", allPlans);
      setApiPlans(allPlans?.filter((plan) => plan.type === "api") ?? []);
      // console.log("!@#!@#!@# ", {apiPlans});
    }
  }, [isSuccess, allPlans]);

  const standardPlan = useMemo(() => {
    return apiPlans?.find((plan) =>
      plan.planName.toLowerCase().includes("standard"),
    );
  }, [apiPlans]);

  const premiumPlan = useMemo(() => {
    return apiPlans?.find((plan) =>
      plan.planName.toLowerCase().includes("premium"),
    );
  }, [apiPlans]);

  const plans = [
    {
      planName: "Free",
      desc: t("Test AI video creation risk-free with basic features."),
      credit: 10,
      price: "",
      includeTitle: t("Basic API Scope"),
      includes: [
        t("Create {{avatar}} Custom Avatar", {
          avatar: 1,
        }),
        t("Create Photo Avatars"),
        t("Create Video from Existing Project"),
        t("Create Video from Templates"),
        t("Create Video from Text, URL, Docs"),
        t("Fetch Avatar, Voice, Template Lists"),
        t("Generative API"),
        t("Watermark Included"),
      ],
    },
    {
      planName: "Standard",
      desc: t("Launch your AI video project with full creative control."),
      credit: standardPlan?.constraints?.monthly?.apiCredit,
      price: `$${standardPlan?.pricePerMonth}`,
      includeTitle: t("Includes"),
      includes: [
        t("Everything in Free"),
        t("Create {{avatars}} Custom Avatars", {
          avatars: 3,
        }),
        t("Watermark Removed"),
      ],
      equalTitle: t("1 Credit Equals"),
      equals: [
        t("{{minutes}}-min Video with Avatar", {
          minutes: 2,
        }),
        t("{{minutes}}-min Audio Export", {
          minutes: 4,
        }),
        t("{{video}} Generative Video ({{seconds}}sec)", {
          video: 1,
          seconds: 5,
        }),
        t("{{images}} Generative Images", {
          images: 6,
        }),
      ],
    },
    {
      planName: "Premium",
      desc: t(
        "Scale production with high-capacity credits and powerful tools.",
      ),
      credit: premiumPlan?.constraints?.monthly?.apiCredit,
      price: `$${premiumPlan?.pricePerMonth}`,
      includeTitle: t("Includes"),
      includes: [
        t("Everything in Standard"),
        t("Create {{avatars}} Custom Avatars", {
          avatars: 5,
        }),
        t("AI Dubbing API"),
      ],
      equalTitle: t("1 Credit Equals"),
      equals: [
        t("{{seconds}}-sec AI Dubbing", {
          seconds: 20,
        }),
      ],
    },
    {
      planName: "Enterprise",
      desc: t("Custom infrastructure and support tailored for your business."),
      credit: null,
      price: null,
      includeTitle: t("Includes"),
      includes: [
        t("Everything in Premium"),
        t("Flexible Custom Avatar"),
        t("Customized Scalability"),
        t("Dedicated Developer Support"),
      ],
    },
  ];

  return (
    <Modal
      width={1240}
      centered
      open={openedModal === MODAL_NAME.API_SUBSCRIPTION_LIST}
      footer={null}
      onCancel={closeModal}
      zIndex={1002}
      destroyOnClose={true}
    >
      <StyledHeader>
        <StyledBoldHeaderTitle>API Plan</StyledBoldHeaderTitle>
        {/* 
          TODO: 기본 구독 플랜 리스트로 교체 버튼은 임시로 숨김\
          TODO: API 플랜이 확정되면 다시 부활 예정
        */}
        {/* <StyledSwitchButton onClick={() => changeToAppPlan()}>
          {t("Looking for AI Studios Plans?")}
        </StyledSwitchButton> */}
      </StyledHeader>

      <StyledContainer>
        {currentPlan &&
          isSuccess &&
          plans.map((plan, index) => (
            <ApiPriceCell
              {...plan}
              key={index}
              currentPlan={currentPlan}
              allPlans={allPlans}
              t={t}
              startSubscription={startSubscription}
              upgradeSubscription={upgradeSubscription}
              downgradeSubscription={downgradeSubscription}
            />
          ))}
      </StyledContainer>
    </Modal>
  );
};
